import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { createLocalizedUrl, removeLanguagePrefix, validateLanguageCode } from '../../utils/languageUtils';

const LANGUAGES = [
  { code: 'en', nativeName: 'English' },
  { code: 'es', nativeName: 'Español' },
  { code: 'fr', nativeName: 'Français' },
  { code: 'de', nativeName: 'Deutsch' },
  { code: 'pt', nativeName: 'Português' }
];

const ICONS = {
  es: '#flag-es',
  en: '#flag-en-gb',
  fr: '#flag-fr',
  de: '#flag-de',
  pt: '#flag-pt'
};

// Header-friendly, layout-neutral language selector (menu semantics)
const LanguageSelector = ({ className = '', showLabel = false, compact = false, openOnHover = false, dropdownPosition = 'bottom' }) => {
  const { i18n, t } = useTranslation();
  const { currentLanguage, changeLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(
    Math.max(0, LANGUAGES.findIndex(l => l.code === currentLanguage))
  );

  const triggerRef = useRef(null);
  const listRef = useRef(null);
  const statusRef = useRef(null);

  // Sync active index with currentLanguage
  useEffect(() => {
    const idx = LANGUAGES.findIndex(l => l.code === currentLanguage);
    if (idx >= 0) setActiveIndex(idx);
  }, [currentLanguage]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      const t = e.target;
      if (listRef.current?.contains(t) || triggerRef.current?.contains(t)) return;
      setOpen(false);
      triggerRef.current?.focus();
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const announce = (msgKey, vars) => {
    if (statusRef.current) {
      statusRef.current.textContent = t(msgKey, vars);
      setTimeout(() => {
        if (statusRef.current) statusRef.current.textContent = '';
      }, 1500);
    }
  };

  const goToLang = async (nextLang) => {
    const langObj = LANGUAGES.find(l => l.code === nextLang) || LANGUAGES[0];
    announce('language.loading', { lang: langObj.nativeName });
    try {
      // Persist preference (cookie preferred for SSR)
      const oneYear = 60*60*24*365;
      const isSecure = window.location.protocol === 'https:';
      document.cookie = `preferred_locale=${nextLang};path=/;max-age=${oneYear};SameSite=Lax${isSecure ? ';Secure' : ''}`;
      try { localStorage.setItem('preferred_locale', nextLang); } catch {}
      try { localStorage.setItem('selectedLanguage', nextLang); } catch {}

      // Update language contexts
      changeLanguage(nextLang);
      if (i18n?.changeLanguage) await i18n.changeLanguage(nextLang);
      try { document.documentElement.setAttribute('lang', nextLang); } catch {}

      // Always navigate to the localized URL to update the URL in the browser
      const basePath = removeLanguagePrefix(location.pathname || '/');
      const localizedPath = createLocalizedUrl(basePath || '/', nextLang);
      const fullUrl = `${localizedPath}${location.search || ''}${location.hash || ''}`;
      
      // Navigate to the localized path (this will update the URL)
      navigate(fullUrl, { replace: true });

      // Analytics hook (optional)
      if (window.gtag && nextLang !== currentLanguage) {
        window.gtag('event', 'language_change', {
          language: nextLang,
          from_language: currentLanguage,
          page_path: location.pathname
        });
      }

      // HTML attributes for AT/SEO and RTL awareness
      try {
        const rtl = ['ar','he','fa','ur'];
        document.documentElement.setAttribute('lang', nextLang);
        document.documentElement.setAttribute('dir', rtl.includes(nextLang) ? 'rtl' : 'ltr');
      } catch {}

      announce('language.switched', { lang: langObj.nativeName });
    } catch (err) {
      console.error('Language change failed:', err);
      announce('language.failed');
    }
  };

  const onTriggerKeyDown = (e) => {
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setOpen(true);
      const idx = Math.max(0, LANGUAGES.findIndex(l => l.code === currentLanguage));
      setActiveIndex(idx);
      requestAnimationFrame(() => {
        const el = document.getElementById(`lang-${LANGUAGES[idx].code}`);
        el?.focus();
      });
    }
  };

  const onListKeyDown = (e) => {
    if (e.key === 'Tab') { setOpen(false); return; }
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'PageDown') {
      e.preventDefault();
      const next = (activeIndex + 1) % LANGUAGES.length;
      setActiveIndex(next);
      document.getElementById(`lang-${LANGUAGES[next].code}`)?.focus();
      return;
    }
    if (e.key === 'ArrowUp' || e.key === 'PageUp') {
      e.preventDefault();
      const prev = (activeIndex - 1 + LANGUAGES.length) % LANGUAGES.length;
      setActiveIndex(prev);
      document.getElementById(`lang-${LANGUAGES[prev].code}`)?.focus();
      return;
    }
    if (e.key === 'Home') {
      e.preventDefault();
      setActiveIndex(0);
      document.getElementById(`lang-${LANGUAGES[0].code}`)?.focus();
      return;
    }
    if (e.key === 'End') {
      e.preventDefault();
      const last = LANGUAGES.length - 1;
      setActiveIndex(last);
      document.getElementById(`lang-${LANGUAGES[last].code}`)?.focus();
      return;
    }
  };

  const current = LANGUAGES.find(l => l.code === currentLanguage) || LANGUAGES[0];

  return (
    <div className={`${className}`}>
        {showLabel && (
          <label htmlFor="lang-trigger" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2.5">{t('language.label', 'Language')}</label>
        )}

        <div
          className="relative inline-block z-50"
          onMouseEnter={openOnHover ? () => setOpen(true) : undefined}
          onMouseLeave={openOnHover ? () => setOpen(false) : undefined}
        >
          <button
            id="lang-trigger"
            ref={triggerRef}
            type="button"
            aria-haspopup="menu"
            aria-expanded={open}
            aria-controls="lang-menu"
            aria-label="Change language"
            onClick={() => setOpen(o => !o)}
            onKeyDown={onTriggerKeyDown}
            className={`inline-flex items-center relative rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm font-medium text-gray-900 dark:text-gray-100 shadow-sm
                       hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                       transition-all duration-200 ${compact ? 'pl-6 pr-5 py-1.5 min-h-[32px] text-xs' : 'pl-10 pr-9 py-3 min-h-[44px] rounded-xl'}`}
          >
            <span className={`absolute top-1/2 -translate-y-1/2 ${compact ? 'left-1.5 h-3.5 w-3.5' : 'left-3 h-5 w-5'}`} aria-hidden="true">
              <svg className={compact ? 'h-3.5 w-3.5 text-gray-600 dark:text-gray-400' : 'h-5 w-5 text-gray-600 dark:text-gray-400'}>
                <use href={ICONS[current.code] || '#icon-globe'} />
              </svg>
            </span>
            <span className={compact ? 'text-xs' : ''}>{current.nativeName}</span>
            <span className={`pointer-events-none absolute inset-y-0 right-0 flex items-center ${compact ? 'pr-1.5' : 'pr-3'}`}>
              <svg className={compact ? 'h-3 w-3 text-gray-500 dark:text-gray-400' : 'h-4 w-4 text-gray-500 dark:text-gray-400'} viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
          </button>

          {open && (
            <div
              id="lang-menu"
              ref={listRef}
              role="menu"
              aria-label="Select language"
              tabIndex={-1}
              onKeyDown={onListKeyDown}
              onMouseEnter={openOnHover ? () => setOpen(true) : undefined}
              onMouseLeave={openOnHover ? () => setOpen(false) : undefined}
              className={`absolute ${dropdownPosition === 'top' ? 'bottom-full mb-2 right-0' : 'left-0 top-full'} ${openOnHover && dropdownPosition !== 'top' ? 'mt-0' : dropdownPosition !== 'top' ? 'mt-2' : ''} w-max max-h-72 overflow-auto bg-white dark:bg-gray-800 shadow-2xl rounded-xl py-2 text-base ring-1 ring-black dark:ring-gray-700 ring-opacity-5 focus:outline-none sm:text-sm border border-gray-100 dark:border-gray-700 z-50`}
            >
              {LANGUAGES.map((l) => {
                const selected = l.code === currentLanguage;
                return (
                  <button
                    key={l.code}
                    id={`lang-${l.code}`}
                    role="menuitemradio"
                    aria-checked={selected}
                    aria-current={selected ? 'true' : undefined}
                    className={`${selected ? 'text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100'}
                             w-full text-left px-3.5 py-2.5 flex items-center gap-3 transition-colors focus:outline-none focus:bg-gray-100 dark:focus:bg-gray-700`}
                    onClick={() => { setOpen(false); triggerRef.current?.focus(); goToLang(l.code); }}
                  >
                    <svg className="h-5 w-7 text-gray-600 dark:text-gray-400 flex-shrink-0" aria-hidden="true">
                      <use href={ICONS[l.code] || '#icon-globe'} />
                    </svg>
                    <span className="font-medium">{l.nativeName}</span>
                    {selected && <span className="ml-auto text-xs text-blue-600 dark:text-blue-400">{t('language.selected', 'Selected')}</span>}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div ref={statusRef} role="status" className="sr-only" aria-live="polite" />
      </div>
  );
};

// Icon-only language selector (perfect for headers)
export const IconLanguageSelector = ({ className = '' }) => {
  const { i18n } = useTranslation();
  const { currentLanguage, changeLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        listRef.current && !listRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const goToLang = async (nextLang) => {
    const oneYear = 60*60*24*365;
    const isSecure = window.location.protocol === 'https:';
    document.cookie = `preferred_locale=${nextLang};path=/;max-age=${oneYear};SameSite=Lax${isSecure ? ';Secure' : ''}`;
    try { localStorage.setItem('preferred_locale', nextLang); } catch {}
    try { localStorage.setItem('selectedLanguage', nextLang); } catch {}
    changeLanguage(nextLang);
    if (i18n?.changeLanguage) await i18n.changeLanguage(nextLang);
    try {
      const rtl = ['ar','he','fa','ur'];
      document.documentElement.setAttribute('lang', nextLang);
      document.documentElement.setAttribute('dir', rtl.includes(nextLang) ? 'rtl' : 'ltr');
    } catch {}
    
    // Always navigate to the localized URL to update the URL in the browser
    const basePath = removeLanguagePrefix(location.pathname || '/');
    const localizedPath = createLocalizedUrl(basePath || '/', nextLang);
    const fullUrl = `${localizedPath}${location.search || ''}${location.hash || ''}`;
    
    // Navigate to the localized path (this will update the URL)
    navigate(fullUrl, { replace: true });
    
    if (window.gtag && nextLang !== currentLanguage) {
      window.gtag('event', 'language_change', { language: nextLang, from_language: currentLanguage, page_path: location.pathname });
    }
  };

  // Validate and normalize the current language
  const validLanguage = validateLanguageCode(currentLanguage);
  
  // Find the current language object, with fallback to English
  const current = LANGUAGES.find(l => l.code === validLanguage) || LANGUAGES[0];
  
  // Use the validated language code for display
  const displayCode = validLanguage;

  return (
    <div
      className={`relative ${className}`}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
        <button
          ref={triggerRef}
          type="button"
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={`Change language - Current: ${current.nativeName}`}
          onClick={() => setOpen(o => !o)}
          className="flex items-center gap-1.5 px-2 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
          title={current.nativeName}
        >
          <svg className="h-5 w-5 text-gray-700 dark:text-gray-300" aria-hidden="true">
            <use href={ICONS[displayCode] || '#icon-globe'} />
          </svg>
          <span className="text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">{displayCode}</span>
        </button>

        {open && (
          <div
            ref={listRef}
            role="menu"
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
            className="absolute right-0 top-full mt-0 w-48 bg-white dark:bg-gray-800 shadow-xl rounded-lg py-2 text-base ring-1 ring-black dark:ring-gray-700 ring-opacity-5 focus:outline-none border border-gray-100 dark:border-gray-700 z-50"
          >
            {LANGUAGES.map((l) => {
              const selected = l.code === currentLanguage;
              return (
                <button
                  key={l.code}
                  role="menuitemradio"
                  aria-checked={selected}
                  className={`${selected ? 'text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}
                           w-full text-left px-4 py-2.5 flex items-center gap-3 transition-colors focus:outline-none focus:bg-gray-100 dark:focus:bg-gray-700`}
                  onClick={() => { setOpen(false); goToLang(l.code); }}
                >
                  <svg className="h-5 w-5 flex-shrink-0" aria-hidden="true">
                    <use href={ICONS[l.code] || '#icon-globe'} />
                  </svg>
                  <span className="font-medium text-sm">{l.nativeName}</span>
                  {selected && <span className="ml-auto text-xs text-blue-600 dark:text-blue-400">✓</span>}
                </button>
              );
            })}
          </div>
        )}
      </div>
  );
};

// Footer-native select (SSR / no-JS friendly)
export const FooterLanguageSelect = ({ className = '' }) => {
  return (
    <div className={className}>
      <LanguageSelector showLabel className="w-full" openOnHover={false} dropdownPosition="top" />
    </div>
  );
};

export default LanguageSelector;
